// ABOUTME: This component displays and manages the surcharges of a shipper or a shipper profile
// ABOUTME: Provides CRUD operations through modals and displays data in a grid

import { Box, IconButton } from "@mui/material";
import { useTheme } from "@mui/material";

import { tokens } from "../../../../../theme";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import ShTextSkeleton from "../../../../theme/skeleton/ShTextSkeleton";
import { useGetSurchargesQuery, useDeleteSurchargeMutation, useAddSurchargeMutation, useUpdateSurchargeMutation } from "../../../../../newapi/surcharge/surchargeSlice";
import { useGetInvoiceProductsSelectQuery } from "../../../../../newapi/financial/invoiceProductSlice";
import AddIcon from '@mui/icons-material/Add';
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import MDynamicValues from "../../../Modals/MDynamicValues";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { showToastMessageDeleteRequest, showToastMessagePostRequest, showToastMessageUpdateRequest, showToastMessageRequestError } from "../../../../../utils/toasts";
import ShDataGrid2 from "../../../../theme/datagrid2/ShDataGrid2";
import MConfirmDelete from "../../../Modals/MConfirmDelete";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import { useSelector } from "react-redux";
import Text from "../../../../theme/text/Text";
import { countriesFromLocale } from "../../../../../utils/countries";
import { surcharge_calculation_type } from "../../../../../utils/staticEnums";
import Subheading from "../../../../theme/text/Subheading";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const GridActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return (
        <ButtonGroup version={2}>
            {props.hasModifyPrivilege && (
            <Shbutton
                className={"group-btn"}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                onClick={() => props.handleOpen(true)}
                endIcon={<AddIcon/>}
                variant="contained"
            >
                {t('new_surcharge')}
            </Shbutton>
            )}
        </ButtonGroup>
    )
}

const Surcharges = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [surchargeInfo, setSurchargeInfo] = useState(null);

    // A surcharge is either linked to a shipper or to a shipper profile
    const owner = props.shipperProfileId ? {shipperProfileId: props.shipperProfileId} : {shipperId: props.shipperId};
    const [config,setConfig] = useState(owner);

    // Only the invoice products that are meant for surcharges can be linked
    const { data: invoiceProducts } = useGetInvoiceProductsSelectQuery("surcharges");

    // Only the field that belongs to the chosen calculation type is shown, the others are hidden
    const hidePercentage = (values) => values.calculationType !== surcharge_calculation_type.PERCENTAGE;
    const hideWeightUnit = (values) => values.calculationType !== surcharge_calculation_type.PER_WEIGHT;

    // A surcharge is calculated in one of three ways, the type decides which of the fields below it uses
    const calculationTypeOptions = useMemo(() => [
        {id: surcharge_calculation_type.PERCENTAGE, name: t('surcharge_type_percentage')},
        {id: surcharge_calculation_type.FIXED, name: t('surcharge_type_fixed')},
        {id: surcharge_calculation_type.PER_WEIGHT, name: t('surcharge_type_weight')}
    ], [t]);

    const language = useSelector((state) => state.settings.language);
    const countryOptions = useMemo(() => (countriesFromLocale(language) ?? []).map(country => ({id: country.alpha2, name: country.name})), [language]);

    const getCountryNames = (countryCodes) => (countryCodes ?? []).map(code => countryOptions.find(country => country.id === code)?.name ?? code);

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.SHIPPER_MANAGE])
    );

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);

    const handleCloseAdd = () => {
        setAddModalOpen(false);
    };

    const handleCloseUpdate = () => {
        setUpdateModalOpen(false);
        setSurchargeInfo(null);
    };

    const [surchargeId, setSurchargeId] = useState(null);
    const handleDeleteSurchargeClick = (id) => {
        setSurchargeId(id);
    }

    useEffect(() => {
        if (surchargeId) {
            setConfirmDeleteModalOpen(true);
        }
    }, [surchargeId]);

    const [DeleteSurcharge,{isLoading: deleteLoading}] = useDeleteSurchargeMutation();
    const handleDeleteSurcharge = async () => {
        try {
           const response = await DeleteSurcharge(surchargeId).unwrap();
           showToastMessageDeleteRequest(t, response);
        } catch (err) {
            showToastMessageDeleteRequest(t, {success: false, error: err.error});
        }
    };

    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const confirmDeleteModal = {
        open: confirmDeleteModalOpen,
        setOpen: setConfirmDeleteModalOpen,
        confirm: handleDeleteSurcharge
    }

    useEffect(() => {
        if (surchargeInfo) {
            setUpdateModalOpen(true);
        }
    }, [surchargeInfo]);

    const [addSurcharge, {isLoading: addLoading}] = useAddSurchargeMutation();
    const handleAddSurcharge = async (values) => {
        try {
            if (addLoading) return;

            const surchargeData = {
                ...owner,
                description: values.description,
                percentage: values.percentage,
                invoiceProductId: values.invoiceProductId,
                allowedCountries: values.allowedCountries ?? [],
                rejectedCountries: values.rejectedCountries ?? [],
                calculationType: values.calculationType,
                weightUnit: values.weightUnit,
                startDate: values.startDate ?? null,
                endDate: values.endDate ?? null
            };

           const response = await addSurcharge(surchargeData).unwrap();
           showToastMessagePostRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t('create_failed'), t, err);
        }
    };

    const [updateSurcharge, {isLoading: updateLoading}] = useUpdateSurchargeMutation();
    const handleUpdateSurcharge = async (values) => {
        try {
            if (updateLoading) return;

            const surchargeData = {
                id: surchargeInfo.id,
                description: values.description,
                percentage: values.percentage,
                invoiceProductId: values.invoiceProductId,
                allowedCountries: values.allowedCountries ?? [],
                rejectedCountries: values.rejectedCountries ?? [],
                calculationType: values.calculationType,
                weightUnit: values.weightUnit,
                startDate: values.startDate ?? null,
                endDate: values.endDate ?? null
            };

           const response = await updateSurcharge(surchargeData).unwrap();
           showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t('update_failed'), t, err);
        }
    };

    const addFields = [
        {
            name: "description",
            label: "description",
            type: "text",
            required: true
        },
        {
            name: "calculationType",
            label: "type",
            type: "list",
            options: calculationTypeOptions,
            defaultValue: surcharge_calculation_type.PERCENTAGE,
            required: true
        },
        {
            name: "percentage",
            label: "percentage",
            type: "integer",
            defaultValue: 0,
            hidden: hidePercentage
        },
        {
            name: "weightUnit",
            label: "per_x_gram",
            type: "integer",
            defaultValue: 0,
            hidden: hideWeightUnit,
            explainerTitle: t('per_x_gram'),
            explainerSubTitle: "surcharge_weight_unit_explainer"
        },
        {
            name: "invoiceProductId",
            label: "invoice_product",
            type: "list",
            options: invoiceProducts ?? [],
            search: true,
            required: true
        },
        {
            name: "allowedCountries",
            label: "allowed_countries",
            type: "list",
            options: countryOptions,
            multiple: true,
            search: true
        },
        {
            name: "rejectedCountries",
            label: "rejected_countries",
            type: "list",
            options: countryOptions,
            multiple: true,
            search: true
        },
        {
            name: "startDate",
            label: "start_date",
            type: "date"
        },
        {
            name: "endDate",
            label: "end_date",
            type: "date"
        }
    ];

    const updateFields = surchargeInfo ? [
        {
            name: "description",
            label: "description",
            type: "text",
            defaultValue: surchargeInfo.description,
            required: true
        },
        {
            name: "calculationType",
            label: "type",
            type: "list",
            options: calculationTypeOptions,
            defaultValue: surchargeInfo.calculationType ?? surcharge_calculation_type.PERCENTAGE,
            required: true
        },
        {
            name: "percentage",
            label: "percentage",
            type: "integer",
            defaultValue: surchargeInfo.percentage,
            hidden: hidePercentage
        },
        {
            name: "weightUnit",
            label: "per_x_gram",
            type: "integer",
            defaultValue: surchargeInfo.weightUnit ?? 0,
            hidden: hideWeightUnit,
            explainerTitle: t('per_x_gram'),
            explainerSubTitle: "surcharge_weight_unit_explainer"
        },
        {
            name: "invoiceProductId",
            label: "invoice_product",
            type: "list",
            options: invoiceProducts ?? [],
            search: true,
            defaultValue: surchargeInfo.invoiceProductId,
            required: true
        },
        {
            name: "allowedCountries",
            label: "allowed_countries",
            type: "list",
            options: countryOptions,
            multiple: true,
            search: true,
            defaultValue: surchargeInfo.allowedCountries ?? []
        },
        {
            name: "rejectedCountries",
            label: "rejected_countries",
            type: "list",
            options: countryOptions,
            multiple: true,
            search: true,
            defaultValue: surchargeInfo.rejectedCountries ?? []
        },
        {
            name: "startDate",
            label: "start_date",
            type: "date",
            defaultValue: surchargeInfo.startDate ?? null
        },
        {
            name: "endDate",
            label: "end_date",
            type: "date",
            defaultValue: surchargeInfo.endDate ?? null
        }
    ] : [];

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions handleOpen={() => setAddModalOpen(true)} hasModifyPrivilege={hasModifyPrivilege}/>,
            gridOptions:gridOptions,
        });

        return () => {
            // cleanup
        }
    },[]);

    const columns = [
        {
            field: "description",
            headerName: t('description'),
            flex:2,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                <Text>
                    {params.row.description}
                </Text>
                );
            }
        },
        {
            field: "calculationType",
            headerName: t('type'),
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                const calculationType = params.row.calculationType ?? surcharge_calculation_type.PERCENTAGE;
                return (
                <Text>
                    {calculationTypeOptions.find(option => option.id === calculationType)?.name ?? calculationType}
                </Text>
                );
            }
        },
        {
            field: "value",
            headerName: t('value'),
            flex:1,
            sortable: false,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                // A fixed surcharge is billed with the price of its own rate, which the rate column
                // already shows, so there is no number of its own to put here
                switch (params.row.calculationType ?? surcharge_calculation_type.PERCENTAGE) {
                    case surcharge_calculation_type.PER_WEIGHT:
                        return <Text>{t('per_weight_unit', {weight: params.row.weightUnit ?? 0})}</Text>;
                    case surcharge_calculation_type.FIXED:
                        return null;
                    default:
                        return <Text>{`${params.row.percentage}%`}</Text>;
                }
            }
        },
        {
            field: "invoiceProduct",
            headerName: t('invoice_product'),
            flex:1.5,
            sortable: false,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                <Text>
                    {params.row.invoiceProduct?.name ?? ""}
                </Text>
                );
            }
        },
        {
            field: "period",
            headerName: t('period'),
            flex:1.2,
            sortable: false,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                // Without dates the surcharge applies to every order, so there is no period to show
                if (!params.row.startDate && !params.row.endDate) {
                    return null;
                }

                const formatDate = (date) => date ? new Date(date).toLocaleDateString(i18n.language) : "";

                return (
                <Text>
                    {`${formatDate(params.row.startDate)} - ${formatDate(params.row.endDate)}`}
                </Text>
                );
            }
        },
        {
            field: "countries",
            headerName: t('countries'),
            flex:1.5,
            sortable: false,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                const allowedCountries = params.row.allowedCountries ?? [];
                const rejectedCountries = params.row.rejectedCountries ?? [];

                // Without countries the surcharge applies everywhere, so there is nothing to show
                if (allowedCountries.length === 0 && rejectedCountries.length === 0) {
                    return null;
                }

                return (
                    <SHQuickAction
                        component={
                            <Box sx={{ml: 0.5}}>
                                {allowedCountries.length > 0 &&
                                    <>
                                        <Subheading sx={{mt: 0.5}}>{t('allowed_countries')}</Subheading>
                                        <Box sx={{mt: 1, mb: 2, maxHeight: 500, width: 300, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px"}}>
                                            {getCountryNames(allowedCountries).map((name, idx) => (
                                                <Box key={idx}>
                                                    <Text>{name}</Text>
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                }
                                {rejectedCountries.length > 0 &&
                                    <>
                                        <Subheading sx={{mt: 0.5}}>{t('rejected_countries')}</Subheading>
                                        <Box sx={{mt: 1, mb: 2, maxHeight: 500, width: 300, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px"}}>
                                            {getCountryNames(rejectedCountries).map((name, idx) => (
                                                <Box key={idx}>
                                                    <Text>{name}</Text>
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                }
                            </Box>
                        }
                    >
                        {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart}) => (
                            <Box
                                aria-owns={anchorEl ? "simple-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {allowedCountries.length > 0 &&
                                    <Text>{t('countries_allowed', {count: allowedCountries.length})}</Text>
                                }
                                {rejectedCountries.length > 0 &&
                                    <Text>{t('countries_rejected', {count: rejectedCountries.length})}</Text>
                                }
                            </Box>
                        )}
                    </SHQuickAction>
                );
            }
        },
        {
            field: "actions",
            headerName: "",
            flex:1,
            sortable: false,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return <Box>
                    {hasModifyPrivilege && (
                        <>
                    <IconButton type="button" onClick={() => setSurchargeInfo(params.row)}
                    sx={{
                        color: colors.txt["primary"]
                    }}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton type="button" onClick={() => handleDeleteSurchargeClick(params.row.id)}
                    sx={{
                        color: colors.red[400]
                    }}>
                        <DeleteIcon/>
                    </IconButton>
                </>
                    )}
            </Box>
            }
        }
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetSurchargesQuery}
                itemId={props.shipperProfileId ?? props.shipperId}
                config={config}
                gridActions={<GridActions handleOpen={() => setAddModalOpen(true)} hasModifyPrivilege={hasModifyPrivilege}></GridActions>}
                title={t('surcharges')}
                gridOptions={gridOptions}
                isSidepanelGrid
                columns={columns}
                rows={data}>
            </ShDataGrid2>

            <MDynamicValues
                open={addModalOpen}
                handleClose={handleCloseAdd}
                title={t("add_surcharge")}
                fields={addFields}
                onConfirm={handleAddSurcharge}
                labelWidth={140}
            />

            <MDynamicValues
                open={updateModalOpen}
                handleClose={handleCloseUpdate}
                title={t("update_surcharge")}
                fields={updateFields}
                onConfirm={handleUpdateSurcharge}
                labelWidth={140}
            />

            <MConfirmDelete
                open={confirmDeleteModal.open}
                handleClose={() => confirmDeleteModal.setOpen(false)}
                data={confirmDeleteModal.data}
                confirm={confirmDeleteModal.confirm}
            />
        </Box>
    );
}

export default Surcharges;
